/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * CoachApprovalCard - Card component for guardians to approve/decline coach requests
 */

import { useState } from 'react'
import { UserCheck, X, Check, Loader2, User, Building2, Star, Award } from 'lucide-react'
import coachService from '../services/coachService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('CoachApprovalCard')

const CoachApprovalCard = ({ request, onApproved, onDeclined }) => {
  const [approving, setApproving] = useState(false)
  const [declining, setDeclining] = useState(false)

  const handleApprove = async () => {
    setApproving(true)
    try {
      const response = await coachService.approveCoachForChild(request.id)
      if (response.success) {
        toast.success('Coach approved!')
        onApproved?.(request.id)
      }
    } catch (error) {
      logger.error('Error approving coach:', error)
      toast.error(error.response?.data?.message || 'Failed to approve coach')
    } finally {
      setApproving(false)
    }
  }

  const handleDecline = async () => {
    setDeclining(true)
    try {
      const response = await coachService.declineCoachForChild(request.id)
      if (response.success) {
        toast.success('Coach request declined')
        onDeclined?.(request.id)
      }
    } catch (error) {
      logger.error('Error declining coach:', error)
      toast.error(error.response?.data?.message || 'Failed to decline coach')
    } finally {
      setDeclining(false)
    }
  }

  const coach = request.coach
  const child = request.client
  const coachProfile = coach?.coachProfile

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 bg-white dark:bg-gray-800">
      <div className="flex items-start gap-4">
        {/* Coach Avatar */}
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center flex-shrink-0">
          <User className="w-6 h-6 text-white" />
        </div>

        <div className="flex-1 min-w-0">
          {/* Coach Info */}
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                {coach?.firstName} {coach?.lastName}
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">{coach?.email}</p>
            </div>
            {coachProfile?.verificationLevel && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium">
                <Award className="w-3 h-3" />
                {coachProfile.verificationLevel}
              </span>
            )}
          </div>

          {/* Coach Profile Details */}
          {coachProfile?.headline && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{coachProfile.headline}</p>
          )}

          {/* Ratings */}
          {coachProfile?.averageRating && (
            <div className="flex items-center gap-1 mt-1">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {coachProfile.averageRating.toFixed(1)}
              </span>
              {coachProfile.totalReviews > 0 && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  ({coachProfile.totalReviews} reviews)
                </span>
              )}
            </div>
          )}

          {/* Organization & Child Info */}
          <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
            {request.organization && (
              <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                <Building2 className="w-4 h-4" />
                {request.organization.name}
              </span>
            )}
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-medium">
              <UserCheck className="w-3 h-3" />
              For{' '}
              {child?.firstName && child?.lastName
                ? `${child.firstName} ${child.lastName}`
                : child?.firstName || child?.name || 'your child'}
            </span>
          </div>

          {/* Bio excerpt */}
          {coachProfile?.bio && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
              {coachProfile.bio}
            </p>
          )}

          {/* Specialties */}
          {coachProfile?.specialties && coachProfile.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {coachProfile.specialties.slice(0, 3).map((specialty, idx) => (
                <span
                  key={idx}
                  className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs"
                >
                  {specialty}
                </span>
              ))}
              {coachProfile.specialties.length > 3 && (
                <span className="px-2 py-0.5 text-gray-500 dark:text-gray-400 text-xs">
                  +{coachProfile.specialties.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 mt-4">
            <button
              onClick={handleApprove}
              disabled={approving || declining}
              className="btn btn-sm btn-primary inline-flex items-center gap-1.5"
            >
              {approving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              Approve
            </button>
            <button
              onClick={handleDecline}
              disabled={approving || declining}
              className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
            >
              {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CoachApprovalCard
